import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  mutation createAdditionalReport($data: createAdditionalReportInput!) {
    createAdditionalReport(data: $data) {
      id
      status
      error {
        message
        statusCode
      }
    }
  }
`;

const initialState: TYPES.MutationResponseState = {
  result: null,
  error: null,
  loading: false
};

export const createAdditionalReportRequest = createAsyncThunk<
  TYPES.MutationResponse,
  {data: TYPES.CreateAdditionalReportInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'additional_report/create_additional_report',
  async (
  params: {data: TYPES.CreateAdditionalReportInput;},
  { extra: client }) =>
  {
    try {
      const response = await client.mutate({
        mutation: GRAPHQL,
        variables: params
      });
      return response.data.createAdditionalReport;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const createAdditionalReportSlice = createSlice({
  name: 'create_additional_report',
  initialState: initialState,
  reducers: {
    createAdditionalReportClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(createAdditionalReportRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(createAdditionalReportRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(createAdditionalReportRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { createAdditionalReportClear } =
createAdditionalReportSlice.actions;

export const createAdditionalReport = createSelector(
  (state: RootState) => state.additionalReport.createAdditionalReport.result,
  (state: RootState) => state.additionalReport.createAdditionalReport.error,
  (state: RootState) =>
  state.additionalReport.createAdditionalReport.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default createAdditionalReportSlice.reducer;